import type { ListMoviesRequest, MovieCard as MovieCardModel } from '@zyx/shared';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Children, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { useMovieList } from '@/hooks/use-movies';
import { cn } from '@/lib/utils';
import { MovieCard } from './movie-card';

/** How wide a card is in a shelf: two and a bit on a phone, up to six on a wide screen. */
const CARD_WIDTH = 'w-[44vw] sm:w-[30vw] md:w-[23vw] lg:w-[18vw] xl:w-[15.5vw] 2xl:w-[14rem]';

/**
 * A titled row of movies that scrolls sideways: by touch or trackpad, by the arrow
 * buttons (which page by the row's width), or by tabbing through its cards, which
 * scroll into view on their own. Snaps to cards, so a row never rests mid-card.
 */
export function ShelfRow(props: { readonly title: string; readonly link?: { readonly to: string; readonly label: string }; readonly children: ReactNode; readonly loading?: boolean }) {
  const row = useRef<HTMLDivElement>(null);
  const count = Children.count(props.children);
  const [edges, setEdges] = useState({ start: true, end: false });

  useEffect(() => {
    const element = row.current;
    if (element === null) {
      return;
    }
    const measure = () => setEdges({ start: element.scrollLeft <= 4, end: element.scrollLeft + element.clientWidth >= element.scrollWidth - 4 });
    measure();
    element.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      element.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [props.loading, count]);

  const page = (direction: 1 | -1) =>
    row.current?.scrollBy({ left: direction * row.current.clientWidth * 0.9, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });

  return (
    <section className="group/shelf animate-rise space-y-3" aria-label={props.title}>
      <div className="flex items-baseline justify-between gap-4">
        <h2 className="font-display text-xl sm:text-2xl">{props.title}</h2>
        {props.link === undefined ? null : (
          <Link to={props.link.to} className="shrink-0 text-sm text-muted-foreground transition-theme hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {props.link.label}
          </Link>
        )}
      </div>
      <div className="relative">
        <div
          ref={row}
          className="-mx-4 flex snap-x snap-mandatory scroll-px-4 gap-4 overflow-x-auto px-4 pb-3 pt-2 [scrollbar-width:none] sm:-mx-6 sm:scroll-px-6 sm:px-6 lg:-mx-8 lg:scroll-px-8 lg:px-8 [&::-webkit-scrollbar]:hidden"
        >
          {props.children}
        </div>
        {(['start', 'end'] as const).map((side) =>
          edges[side] ? null : (
            <button
              key={side}
              type="button"
              tabIndex={-1}
              aria-hidden
              onClick={() => page(side === 'start' ? -1 : 1)}
              className={cn(
                'absolute top-2 hidden h-[calc(100%-1.75rem)] w-12 items-center justify-center text-white opacity-0 transition-theme group-hover/shelf:opacity-100 md:flex',
                side === 'start' ? '-left-4 bg-gradient-to-r from-background to-transparent sm:-left-6 lg:-left-8' : '-right-4 bg-gradient-to-l from-background to-transparent sm:-right-6 lg:-right-8',
              )}
            >
              {side === 'start' ? <ChevronLeft className="size-7 drop-shadow" /> : <ChevronRight className="size-7 drop-shadow" />}
            </button>
          ),
        )}
      </div>
    </section>
  );
}

/** A shelf of movies already in hand. */
export function MovieShelf(props: { readonly title: string; readonly movies: ReadonlyArray<MovieCardModel>; readonly link?: { readonly to: string; readonly label: string } }) {
  return (
    <ShelfRow title={props.title} link={props.link}>
      {props.movies.map((movie) => (
        <MovieCard key={movie.movieId} movie={movie} className={cn('shrink-0 snap-start', CARD_WIDTH)} />
      ))}
    </ShelfRow>
  );
}

/** A shelf of a library query's first page; nothing at all when the query finds nothing. */
export function QueryShelf(props: { readonly title: string; readonly request: Omit<ListMoviesRequest, 'page'>; readonly link?: { readonly to: string; readonly label: string } }) {
  const query = useMovieList({ ...props.request, limit: 24 });
  const movies = query.data?.pages[0]?.movies;
  if (query.isPending) {
    return (
      <ShelfRow title={props.title} loading>
        {Array.from({ length: 6 }, (_unused, index) => (
          <div key={index} className={cn('shrink-0 space-y-2', CARD_WIDTH)}>
            <Skeleton className="aspect-[3/2] w-full rounded-card" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-full" />
          </div>
        ))}
      </ShelfRow>
    );
  }
  if (movies === undefined || movies.length === 0) {
    return null;
  }
  return <MovieShelf title={props.title} movies={movies} link={props.link} />;
}
